// StatUnit.js — Self-contained KPI card: neutral gray-900 number above label.
//
// KPI numbers are NEVER decoratively colored. If a KPI needs a state,
// pass `status` (a status slug) to render a small StatusChip next to the label.
//
// Props:
//   value   - number/string to display (pre-formatted, e.g. via formatCurrency)
//   label   - small uppercase label below the number
//   status  - optional status slug; renders a StatusChip under the value
//   info    - optional tooltip text shown via an info icon next to the label
//   sx      - extra styles merged into the container
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import StatusChip from './StatusChip';

export default function StatUnit({ value, label, status, info, sx = {} }) {
  return (
    <Box
      sx={{
        minWidth: 140,
        p: 2,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: 'grey.200',
        backgroundColor: '#fff',
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: '24px',
          fontWeight: 600,
          lineHeight: 1.2,
          color: 'grey.900',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 500,
            color: 'grey.500',
            textTransform: 'uppercase',
            letterSpacing: 0,
            lineHeight: 1.3,
            whiteSpace: 'normal',
          }}
        >
          {label}
        </Typography>
        {info && (
          <Tooltip title={info} arrow>
            <InfoIcon sx={{ fontSize: 14, color: 'grey.400' }} />
          </Tooltip>
        )}
        {status && <StatusChip status={status} />}
      </Box>
    </Box>
  );
}
